import { extractJsonPaths, resolveJsonPath } from './jsonPathExtractor';
import { getFieldsNodeAtPath } from './fieldsTreeMutations';

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function formatSampleValue(value) {
  if (value === null) {
    return 'null';
  }

  if (value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.length > 48 ? `${value.slice(0, 45)}…` : value;

    return trimmed || '""';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.length}]`;
  }

  if (isPlainObject(value)) {
    return '{…}';
  }

  return String(value);
}

function classifyValue(value) {
  if (Array.isArray(value)) {
    return 'array';
  }

  if (isPlainObject(value)) {
    return 'object';
  }

  return 'scalar';
}

/**
 * Innermost Marmin catalog array ancestor for a leaf path (e.g. document_lines.charges.amount → document_lines.charges).
 */
export function findInnermostArrayAncestor(catalogFields, marminPath) {
  const segments = marminPath.split('.').filter(Boolean);
  if (segments.length < 2 || !catalogFields) {
    return null;
  }

  let current = catalogFields;
  let arrayAncestor = null;

  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    const node = current?.[segment];
    if (!node) {
      return arrayAncestor;
    }

    if (node.kind === 'array') {
      arrayAncestor = segments.slice(0, i + 1).join('.');
    }

    current = node.children;
  }

  return arrayAncestor;
}

export function getArraySourceFromFields(fields, marminArrayPath) {
  const node = getFieldsNodeAtPath(fields, marminArrayPath);

  if (!node || typeof node.source !== 'string' || !node.source) {
    return '';
  }

  return node.source;
}

/**
 * Convert a root ERP path such as $.lines.0.name into an array-item-relative path $.name.
 */
export function toArrayItemRelativeSource(erpPath, arraySourcePath) {
  if (!erpPath || !arraySourcePath) {
    return erpPath;
  }

  if (!erpPath.startsWith(`${arraySourcePath}.`)) {
    return erpPath;
  }

  const parts = erpPath
    .slice(arraySourcePath.length + 1)
    .split('.')
    .filter(Boolean);

  while (parts.length > 0 && /^\d+$/.test(parts[0])) {
    parts.shift();
  }

  if (!parts.length) {
    return '$';
  }

  return `$.${parts.join('.')}`;
}

/**
 * ERP fields inside the first array element, using paths relative to each item (DSL shape).
 */
export function buildArrayItemFieldNodes(payload, arraySourcePath, depth = 0) {
  const resolved = resolveJsonPath(payload, arraySourcePath);
  if (!Array.isArray(resolved) || resolved.length === 0) {
    return [];
  }

  const sampleItem = resolved[0];
  const paths = extractJsonPaths(sampleItem).filter((path) => path !== '$');

  return paths
    .map((path) => {
      const value = resolveJsonPath(sampleItem, path);
      const kind = classifyValue(value);

      if (kind === 'object') {
        return null;
      }

      return {
        path,
        key: path.slice(2),
        kind,
        sample: formatSampleValue(value),
        depth,
        arrayRootPath: arraySourcePath,
        itemField: true,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.path.localeCompare(b.path));
}

export function resolveMappedSourcePath({ erpPath, marminPath, fieldCatalog, fields }) {
  if (!erpPath || !marminPath) {
    return erpPath;
  }

  const arrayAncestor = findInnermostArrayAncestor(fieldCatalog?.fields, marminPath);
  if (!arrayAncestor) {
    return erpPath;
  }

  const arraySource = getArraySourceFromFields(fields, arrayAncestor);
  if (arraySource) {
    return toArrayItemRelativeSource(erpPath, arraySource);
  }

  const indexedMatch = erpPath.match(/^(\$\.[^.]+)\.\d+(?:\.(.+))?$/);
  if (indexedMatch) {
    const inferredArraySource = indexedMatch[1];
    const relative = toArrayItemRelativeSource(erpPath, inferredArraySource);

    return relative;
  }

  return erpPath;
}
